import React from "react";
import styled from "styled-components";
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  Row,
  Select,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import { DASHBOARD_DATE_PRESETS } from "../dashboardFilters.js";
import {
  getActiveRouteCorridors,
  getRouteCorridors,
} from "../../routeCorridors/services/routeCorridorService.js";
import LeadPulseWidget from "./LeadPulseWidget.jsx";

const { Text, Title } = Typography;
const { Option } = Select;

const DATE_PRESET_OPTIONS = [
  { value: DASHBOARD_DATE_PRESETS.TODAY, label: "Today" },
  { value: DASHBOARD_DATE_PRESETS.YESTERDAY, label: "Yesterday" },
  { value: DASHBOARD_DATE_PRESETS.THIS_WEEK, label: "This week" },
  { value: DASHBOARD_DATE_PRESETS.LAST_7_DAYS, label: "Last 7 days" },
  { value: DASHBOARD_DATE_PRESETS.THIS_MONTH, label: "This month" },
  { value: DASHBOARD_DATE_PRESETS.LAST_MONTH, label: "Last month" },
  { value: DASHBOARD_DATE_PRESETS.QUARTER, label: "Quarter" },
  { value: DASHBOARD_DATE_PRESETS.YEAR, label: "Year" },
];

const ShellSection = styled.div`
  margin-bottom: 24px;
`;

const SlotCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const DecisionHero = styled(SlotCard)`
  border: 2px solid
    ${({ $status }) =>
      $status === "validating"
        ? "#95de64"
        : $status === "invalidating"
          ? "#ffa39e"
          : "#ffe58f"};
  background: ${({ $status }) =>
    $status === "validating"
      ? "#f6ffed"
      : $status === "invalidating"
        ? "#fff1f0"
        : "#fffbe6"};
`;

const HealthTile = styled.div`
  border: 2px solid
    ${({ $status }) =>
      $status === "contradicted" || $status === "critical"
        ? "#ffa39e"
        : $status === "early_signal" || $status === "validated"
          ? "#b7eb8f"
          : $status === "not_tested"
            ? "#d9d9d9"
            : "#ffe58f"};
  border-radius: 12px;
  padding: 14px;
  height: 100%;
  background: ${({ $status }) =>
    $status === "contradicted" || $status === "critical"
      ? "#fff1f0"
      : $status === "early_signal" || $status === "validated"
        ? "#f6ffed"
        : $status === "not_tested"
          ? "#fafafa"
          : "#fffbe6"};

  .health-label {
    font-size: 12px;
    color: #5f6b6a;
    margin-bottom: 4px;
  }

  .health-value {
    font-size: 22px;
    font-weight: 700;
    color: #005249;
    line-height: 1.15;
  }
`;

const MetricTile = styled.div`
  border: 1px solid #e6efed;
  border-radius: 10px;
  padding: 12px;
  height: 100%;
  background: #fff;

  .metric-value {
    font-size: 22px;
    font-weight: 700;
    color: #005249;
    line-height: 1.2;
  }

  .metric-label {
    color: #5f6b6a;
    font-size: 12px;
    margin-bottom: 6px;
  }
`;

const MiniFunnel = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-top: 12px;
  font-size: 14px;
`;

const MiniStage = styled.span`
  padding: 6px 10px;
  border-radius: 8px;
  background: ${({ $status }) =>
    $status === "failed" || $status === "contradicted"
      ? "#fff1f0"
      : $status === "not_tested"
        ? "#f5f5f5"
        : "#f7fbfa"};
  border: 1px solid
    ${({ $highlight, $status }) =>
      $highlight
        ? "#ff7875"
        : $status === "not_tested"
          ? "#d9d9d9"
          : "#c5ddd9"};
  font-weight: ${({ $highlight }) => ($highlight ? 700 : 500)};
  color: ${({ $highlight }) => ($highlight ? "#a8071a" : "#005249")};
`;

const SectionEyebrow = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 11px;
  color: #8aa8a3;
  margin-bottom: 4px;
  font-weight: 600;
`;

const RuleColumn = styled.div`
  border-radius: 10px;
  padding: 12px;
  background: ${({ $tone }) => ($tone === "continue" ? "#f6ffed" : "#fff1f0")};
  border: 1px solid ${({ $tone }) => ($tone === "continue" ? "#b7eb8f" : "#ffa39e")};
`;

const ExperimentDashboardShell = ({
  metricGroups,
  dashboardFilters,
  filterControls = null,
  loading = false,
  error = null,
  loadWarning = null,
  canView = false,
}) => {
  if (!canView) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Experiment dashboard access required"
        description="You need dashboard reporting permissions to view Wave 1.5 experiment metrics."
      />
    );
  }

  if (loading) {
    return (
      <ShellSection data-testid="experiment-dashboard-loading">
        <Skeleton active paragraph={{ rows: 8 }} />
      </ShellSection>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Experiment dashboard could not load"
        description={error}
        data-testid="experiment-dashboard-error"
      />
    );
  }

  const experiment = metricGroups?.experiment;
  const experimentStatus = experiment?.status;
  const experimentMissing = !experiment;
  const experimentFailed =
    experimentStatus === "error" || experimentStatus === "forbidden";

  if (experimentMissing || experimentFailed) {
    const warningMessage =
      experiment?.warnings?.[0]?.message ||
      (experimentStatus === "forbidden"
        ? "Missing permissions for Wave 1.5 experiment metrics."
        : "Wave 1.5 experiment metrics were not returned by the API.");
    return (
      <ShellSection data-testid="experiment-dashboard-shell">
        <LeadPulseWidget filters={dashboardFilters} />
        <Alert
          type={experimentStatus === "forbidden" ? "warning" : "error"}
          showIcon
          message={
            experimentStatus === "forbidden"
              ? "Experiment metrics unavailable"
              : "Experiment metrics failed to load"
          }
          description={warningMessage}
          data-testid="experiment-metrics-unavailable"
        />
      </ShellSection>
    );
  }

  const metadata = experiment?.metadata || {};
  const hasDecision = Boolean(metadata.decision?.status);
  const decision = hasDecision
    ? metadata.decision
    : {
        status: "too_early",
        label: "Too Early",
        scaleRecommendation: "Do not scale yet",
        nextAction: "Experiment decision metadata missing — refresh required",
        rationale: "Metrics loaded, but decision payload is incomplete.",
      };
  const sample = metadata.confidence?.sample || {};
  const hypothesis = metadata.hypothesis || {};
  const evidence = metadata.evidence || {};
  const whatChanged = metadata.whatChanged || [];
  const decisionCriteria = metadata.decisionCriteria || null;
  const marketplaceValidation =
    metadata.marketplaceValidation || metadata.marketplaceHealth || {};
  const focus = metadata.providerAcceptanceFocus || {};
  const sections = metadata.sections || {};
  const funnelStages = metadata.marketplaceFunnel?.stages || [];
  const criticalFailure = metadata.marketplaceFunnel?.criticalFailurePoint;
  const exposureMetric = evidence.network?.exposureMetric || {};
  const providerEconomics = metadata.providerEconomics || { available: false };
  const viability = metadata.viability || {};
  const showViability = sections.financialViability || viability.visible;
  const showReliability = sections.reliability || metadata.reliability?.visible;
  const immediateGate = decision.immediateGate || decision.nextGate;
  const fulfilmentGate = decision.fulfilmentValidationGate;
  const evaluateAfter = decision.evaluateAfterImmediate;

  return (
    <ShellSection data-testid="experiment-dashboard-shell">
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        {loadWarning ? (
          <Alert
            type="warning"
            showIcon
            message="Latest refresh failed — showing last loaded experiment data"
            description={loadWarning}
            data-testid="experiment-load-warning"
          />
        ) : null}

        <LeadPulseWidget dashboardFilters={dashboardFilters} filters={dashboardFilters} />

        <ExperimentFilterBar
          dashboardFilters={dashboardFilters}
          filterControls={filterControls}
        />

        <DecisionHero data-testid="experiment-decision" $status={decision.status}>
          <SectionEyebrow>Wave 1.5 · Marketplace validation</SectionEyebrow>
          <Space wrap align="center" style={{ marginBottom: 6 }}>
            <Title level={3} style={{ margin: 0 }}>
              {decisionEmoji(decision.status)} {decision.label || "Too Early"}
            </Title>
            <Tag color={decisionTagColor(decision.status)}>
              {decision.scaleRecommendation || "Do not scale yet"}
            </Tag>
          </Space>
          <Text style={{ display: "block", marginBottom: 10 }}>
            {decision.rationale ||
              hypothesis.primaryQuestion ||
              "Provider acceptance has not yet been validated."}
          </Text>

          <Row gutter={[16, 12]}>
            <Col xs={24} md={12}>
              <Text type="secondary">Immediate gate</Text>
              <div>
                <Text strong data-testid="experiment-next-action">
                  {immediateGate?.label || decision.nextAction}
                </Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Progress {immediateGate?.current ?? sample.providerExposures ?? 0} /{" "}
                {immediateGate?.target ?? sample.nextGateProviderExposures ?? "—"}
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">Then evaluate</Text>
              <div>
                <Text>
                  {evaluateAfter?.continueIf ||
                    `≥${sample.continueInterestPercent ?? 25}% provider interest → continue`}
                </Text>
              </div>
              <div>
                <Text>
                  {evaluateAfter?.redesignIf ||
                    `<${sample.redesignInterestPercent ?? 10}% after gate → redesign`}
                </Text>
              </div>
            </Col>
          </Row>

          {fulfilmentGate ? (
            <Text type="secondary" style={{ display: "block", marginTop: 10, fontSize: 12 }}>
              Fulfilment validation — required before final marketplace validation
              (near-term {fulfilmentGate.current}/{fulfilmentGate.nearTermTarget}
              {fulfilmentGate.fullSampleTarget
                ? `; full sample design ${fulfilmentGate.fullSampleTarget}`
                : ""}
              ).
            </Text>
          ) : null}

          {decisionCriteria ? (
            <Row gutter={[12, 12]} style={{ marginTop: 14 }} data-testid="experiment-decision-criteria">
              <Col xs={24} md={12}>
                <RuleColumn $tone="continue">
                  <Text strong>CONTINUE</Text>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {(decisionCriteria.continueIf || []).map((item) => (
                      <li key={criteriaKey(item)}>
                        <Text>🟢 {criteriaText(item)}</Text>
                      </li>
                    ))}
                  </ul>
                </RuleColumn>
              </Col>
              <Col xs={24} md={12}>
                <RuleColumn $tone="redesign">
                  <Text strong>REDESIGN</Text>
                  <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                    {(decisionCriteria.stopOrRedesignIf || []).map((item) => (
                      <li key={criteriaKey(item)}>
                        <Text>🔴 {criteriaText(item)}</Text>
                      </li>
                    ))}
                  </ul>
                </RuleColumn>
              </Col>
            </Row>
          ) : null}
        </DecisionHero>

        <SlotCard title="Marketplace validation" data-testid="experiment-marketplace-health">
          <SectionEyebrow>Primary = provider acceptance · Supporting = customer funding</SectionEyebrow>
          <Row gutter={[12, 12]} style={{ marginTop: 10 }}>
            <Col xs={24} md={8}>
              <HealthPillar
                testId="health-customer"
                pillar={marketplaceValidation.customer}
                fallbackLabel="Customer funding"
                fractionFirst
              />
            </Col>
            <Col xs={24} md={8}>
              <HealthPillar
                testId="health-provider"
                pillar={marketplaceValidation.provider}
                fallbackLabel="Provider acceptance"
                emphasize
                fractionFirst
              />
            </Col>
            <Col xs={24} md={8}>
              <HealthPillar
                testId="health-fulfilment"
                pillar={marketplaceValidation.fulfilment}
                fallbackLabel="Fulfilment"
                fractionFirst
              />
            </Col>
          </Row>

          <div data-testid="experiment-mini-funnel" style={{ marginTop: 8 }}>
            <SectionEyebrow>Provider marketplace funnel</SectionEyebrow>
            <MiniFunnel>
              {funnelStages.map((stage, index) => (
                <React.Fragment key={stage.key}>
                  {index > 0 ? <Text type="secondary">→</Text> : null}
                  <MiniStage
                    $highlight={Boolean(stage.isLargestLeakage)}
                    $status={stage.evidenceStatus || "observed"}
                  >
                    {stage.isLargestLeakage ? "🔴 " : ""}
                    {stage.count} {stage.label}
                    {stage.evidenceStatus === "not_tested" ? " · Not tested" : ""}
                  </MiniStage>
                </React.Fragment>
              ))}
            </MiniFunnel>
            {criticalFailure ? (
              <Alert
                style={{ marginTop: 10 }}
                type="error"
                showIcon
                message={`Critical failure: ${criticalFailure.label}`}
                description={criticalFailure.summary}
                data-testid="experiment-critical-failure"
              />
            ) : null}
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
              {exposureMetric.label || "Qualified providers exposed to jobs"}:{" "}
              <Text strong>
                {exposureMetric.invitationsSent ?? evidence.network?.invited ?? 0}
              </Text>
              {exposureMetric.opened != null
                ? ` · opened ${exposureMetric.opened} / ${exposureMetric.invitationsSent ?? 0}`
                : ""}
              {exposureMetric.uniqueProviders != null
                ? ` · unique providers ${exposureMetric.uniqueProviders}`
                : ""}
            </Text>
            {exposureMetric.definition ? (
              <Text type="secondary" style={{ fontSize: 11, display: "block" }}>
                {exposureMetric.definition}
              </Text>
            ) : null}
          </div>
        </SlotCard>

        <SlotCard title="Provider acceptance" data-testid="experiment-provider-focus">
          <Space wrap style={{ marginBottom: 8 }}>
            <Title level={4} style={{ margin: 0 }}>
              {focus.behaviorLabel || focus.fractionLabel || "—"}
            </Title>
            <Tag color={evidenceStateColor(focus.evidenceState || focus.status)}>
              {formatEvidenceState(focus.evidenceState || focus.status)}
            </Tag>
            {focus.confidenceLabel ? (
              <Tag>{focus.confidenceLabel} confidence</Tag>
            ) : null}
          </Space>

          <Row gutter={[16, 12]}>
            <Col xs={24} md={12}>
              <Text type="secondary">What we know</Text>
              <div>
                <Text strong>{focus.whatWeKnow || "—"}</Text>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Text type="secondary">What we do not know</Text>
              <div>
                <Text strong>{focus.whatWeDontKnow || "—"}</Text>
              </div>
            </Col>
          </Row>

          {focus.evidenceCollection ? (
            <Alert
              style={{ marginTop: 12 }}
              type="info"
              showIcon
              data-testid="experiment-evidence-collection"
              message={`Evidence collection · ${focus.evidenceCollection.title}`}
              description={
                <>
                  <div>{focus.evidenceCollection.action}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {focus.evidenceCollection.note}
                  </Text>
                </>
              }
            />
          ) : null}

          {focus.nextExperiment ? (
            <div style={{ marginTop: 12 }} data-testid="experiment-next-experiment">
              <Text type="secondary">Then experiment</Text>
              <div>
                <Text strong>{focus.nextExperiment.title}</Text>
              </div>
              <Text type="secondary" style={{ display: "block" }}>
                Hypothesis: {focus.nextExperiment.hypothesis}
              </Text>
              <Text>
                Success criterion: {focus.nextExperiment.successCriterion || focus.successCriterion}
              </Text>
            </div>
          ) : (
            <Text style={{ display: "block", marginTop: 12 }}>
              Success criterion: {focus.successCriterion || "—"}
            </Text>
          )}
        </SlotCard>

        <SlotCard
          title="Provider rejection reasons"
          data-testid="experiment-rejection-reasons"
        >
          <Alert
            type="warning"
            showIcon
            message="No rejection reasons captured yet"
            description={
              focus.evidenceCollection?.action ||
              "Interview providers who opened the opportunity but did not accept. Build the interface that collects missing evidence — do not analyse empty reason codes."
            }
          />
        </SlotCard>

        {providerEconomics.available ? (
          <SlotCard title="Provider economics" data-testid="experiment-provider-economics">
            <SectionEyebrow>Why accept / reject may be economic</SectionEyebrow>
            <Row gutter={[12, 12]} style={{ marginTop: 8 }}>
              <Col xs={8}>
                <MetricTile>
                  <div className="metric-label">Avg customer price</div>
                  <div className="metric-value">
                    {formatMoney(providerEconomics.summary?.avgCustomerPrice)}
                  </div>
                </MetricTile>
              </Col>
              <Col xs={8}>
                <MetricTile>
                  <div className="metric-label">Avg provider payout</div>
                  <div className="metric-value">
                    {formatMoney(providerEconomics.summary?.avgProviderPayout)}
                  </div>
                </MetricTile>
              </Col>
              <Col xs={8}>
                <MetricTile>
                  <div className="metric-label">Avg Bubakii contribution</div>
                  <div className="metric-value">
                    {formatMoney(providerEconomics.summary?.avgContribution)}
                  </div>
                </MetricTile>
              </Col>
            </Row>
            {providerEconomics.funnel ? (
              <Space wrap style={{ marginTop: 12 }} data-testid="provider-economics-funnel">
                <Tag>Invited {providerEconomics.funnel.invited ?? 0}</Tag>
                <Tag>Opened {providerEconomics.funnel.opened ?? 0}</Tag>
                <Tag color="success">Interested {providerEconomics.funnel.interested ?? 0}</Tag>
                <Tag color="error">Declined {providerEconomics.funnel.declined ?? 0}</Tag>
                <Tag>Pending {providerEconomics.funnel.pending ?? 0}</Tag>
              </Space>
            ) : null}
            <Table
              style={{ marginTop: 12 }}
              size="small"
              pagination={false}
              rowKey="invitationId"
              dataSource={providerEconomics.samples || []}
              columns={[
                {
                  title: "Opportunity",
                  key: "opportunity",
                  render: (_, row) =>
                    row.orderId ? (
                      <a href={`/orders/${row.orderId}`}>
                        {row.opportunityReference || row.opportunityId || "—"}
                      </a>
                    ) : (
                      row.opportunityReference || row.opportunityId || "—"
                    ),
                },
                {
                  title: "Route",
                  dataIndex: "route",
                  key: "route",
                  render: (v) => v || "—",
                },
                {
                  title: "Customer",
                  dataIndex: "customerPrice",
                  key: "customerPrice",
                  render: formatMoney,
                },
                {
                  title: "Payout",
                  dataIndex: "providerPayout",
                  key: "providerPayout",
                  render: (v) => (v == null ? "—" : formatMoney(v)),
                },
                {
                  title: "Contribution",
                  dataIndex: "bubakiiContribution",
                  key: "bubakiiContribution",
                  render: (v) => (v == null ? "—" : formatMoney(v)),
                },
                {
                  title: "Margin",
                  dataIndex: "marginPercent",
                  key: "marginPercent",
                  render: (v) => (v == null ? "—" : `${v}%`),
                },
                {
                  title: "Status",
                  dataIndex: "acceptanceLabel",
                  key: "acceptanceLabel",
                  render: (label, row) => (
                    <Tag color={acceptanceTagColor(row.acceptance)}>{label || "—"}</Tag>
                  ),
                },
                {
                  title: "Deadline",
                  key: "deadline",
                  render: (_, row) =>
                    formatDeadlineRemaining(row.deadlineHoursRemaining, row.selectionDeadlineAt),
                },
              ]}
            />
          </SlotCard>
        ) : null}

        <Collapse
          data-testid="experiment-drilldown"
          items={[
            {
              key: "changed",
              label: "What changed",
              children: (
                <div data-testid="experiment-what-changed">
                  {whatChanged.length === 0 ? (
                    <Text type="secondary">No material period-over-period changes yet.</Text>
                  ) : (
                    <Row gutter={[12, 12]}>
                      {whatChanged.map((change) => (
                        <Col xs={12} md={8} lg={6} key={change.key}>
                          <MetricTile>
                            <div className="metric-label">{change.label}</div>
                            <div className="metric-value">
                              {change.currentFraction ||
                                formatMetricDisplay(change.value, change.unit)}
                            </div>
                            {change.previousFraction ? (
                              <Text type="secondary" style={{ fontSize: 11 }}>
                                Prev {change.previousFraction}
                              </Text>
                            ) : null}
                            {change.caution ? (
                              <Text type="warning" style={{ fontSize: 11, display: "block" }}>
                                {change.caution}
                              </Text>
                            ) : null}
                          </MetricTile>
                        </Col>
                      ))}
                    </Row>
                  )}
                </div>
              ),
            },
            {
              key: "supporting",
              label: "Supporting evidence — customer demand",
              children: (
                <div data-testid="experiment-desirability">
                  <Row gutter={[12, 12]}>
                    <Col xs={12} md={6}>
                      <MetricTile>
                        <div className="metric-label">Qualified leads</div>
                        <div
                          className="metric-value"
                          data-testid="desirability-qualified-leads"
                        >
                          {formatNumber(evidence.demand?.qualifiedLeads)}
                        </div>
                      </MetricTile>
                    </Col>
                    <Col xs={12} md={6}>
                      <MetricTile>
                        <div className="metric-label">Quotes sent</div>
                        <div
                          className="metric-value"
                          data-testid="desirability-quotes-sent"
                        >
                          {formatNumber(evidence.demand?.quotesSent)}
                        </div>
                      </MetricTile>
                    </Col>
                    <Col xs={12} md={6}>
                      <MetricTile>
                        <div className="metric-label">Customers funded</div>
                        <div
                          className="metric-value"
                          data-testid="desirability-deposit-conversion"
                        >
                          {evidence.demand?.behaviorLabel ||
                            evidence.demand?.fractionLabel ||
                            "—"}
                        </div>
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {formatPercent(evidence.demand?.depositConversionRate)} conversion
                          {" · "}
                          {evidence.demand?.confidenceLabel || "low"} confidence
                        </Text>
                      </MetricTile>
                    </Col>
                    <Col xs={12} md={6}>
                      <MetricTile>
                        <div className="metric-label">Coverage ≥1 interested</div>
                        <div
                          className="metric-value"
                          data-testid="feasibility-interested-coverage"
                        >
                          {formatPercent(
                            evidence.network?.opportunitiesWithInterestedProviderRate
                          )}
                        </div>
                      </MetricTile>
                    </Col>
                  </Row>
                </div>
              ),
            },
            {
              key: "funnel",
              label: "Provider funnel detail",
              children: (
                <div data-testid="experiment-provider-funnel">
                  <Row gutter={[8, 8]}>
                    {funnelStages.map((stage) => (
                      <Col xs={12} sm={8} md={4} key={stage.key}>
                        <MetricTile>
                          <div className="metric-label">{stage.label}</div>
                          <div className="metric-value">{stage.count}</div>
                          <Tag>{formatStageStatus(stage.evidenceStatus)}</Tag>
                        </MetricTile>
                      </Col>
                    ))}
                  </Row>
                </div>
              ),
            },
          ]}
        />

        {showViability ? (
          <SlotCard title="Financial viability" data-testid="experiment-viability">
            <Text data-testid="viability-estimated-margin">
              Est. margin: {formatPercent(viability.estimatedMarginPercent)}
            </Text>
          </SlotCard>
        ) : null}

        {showReliability ? (
          <SlotCard title="Reliability" data-testid="experiment-reliability">
            <Text>
              Jobs completed: {metadata.reliability?.jobsCompleted ?? 0}
            </Text>
          </SlotCard>
        ) : null}
      </Space>
    </ShellSection>
  );
};

function HealthPillar({
  pillar,
  fallbackLabel,
  testId,
  emphasize = false,
  fractionFirst = false,
}) {
  const status = pillar?.status || "not_tested";
  return (
    <HealthTile $status={status} data-testid={testId}>
      <div className="health-label">
        {pillar?.label || fallbackLabel}
        {emphasize ? " · PRIMARY" : pillar?.role === "supporting" ? " · supporting" : ""}
      </div>
      <div className="health-value">
        {status === "not_tested"
          ? "Not tested"
          : fractionFirst
            ? pillar?.behaviorLabel || pillar?.fractionLabel || formatPercent(pillar?.rate)
            : formatPercent(pillar?.rate)}
      </div>
      {status !== "not_tested" && fractionFirst ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatPercent(pillar?.rate)} conversion
        </Text>
      ) : status !== "not_tested" ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {pillar?.fractionLabel || "—"}
        </Text>
      ) : null}
      <div style={{ marginTop: 8 }}>
        <Tag color={evidenceStateColor(status)}>{formatEvidenceState(status)}</Tag>
        {pillar?.confidenceLabel && pillar.confidenceLabel !== "n/a" ? (
          <Tag>{pillar.confidenceLabel} confidence</Tag>
        ) : null}
      </div>
    </HealthTile>
  );
}

function ExperimentFilterBar({ dashboardFilters, filterControls }) {
  const time = dashboardFilters?.time || {};
  const selectedCorridor =
    dashboardFilters?.operational?.routeCorridor ||
    dashboardFilters?.operational?.route ||
    undefined;
  const leadSource = dashboardFilters?.commercial?.leadSource || "";
  const interactive = Boolean(filterControls);

  const applyCorridor = (value) => {
    const corridor = value || null;
    if (filterControls?.updateDashboardFilters) {
      filterControls.updateDashboardFilters({
        operational: {
          route: corridor,
          routeCorridor: corridor,
        },
      });
      return;
    }
    filterControls?.updateDashboardFilter?.("operational", "routeCorridor", corridor);
    filterControls?.updateDashboardFilter?.("operational", "route", corridor);
  };

  return (
    <SlotCard data-testid="experiment-filter-bar">
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <FilterOutlined style={{ color: "#005249" }} />
            <Text strong>Experiment filters</Text>
          </Space>
          <FilterSummaryText dashboardFilters={dashboardFilters} />
        </Space>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text type="secondary">Date range</Text>
            <Select
              data-testid="experiment-filter-date-preset"
              style={{ width: "100%", marginTop: 4 }}
              value={time.datePreset || DASHBOARD_DATE_PRESETS.LAST_7_DAYS}
              disabled={!interactive}
              onChange={(value) => filterControls?.setTimePreset?.(value)}
            >
              {DATE_PRESET_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text type="secondary">Corridor</Text>
            <CorridorSelect
              value={selectedCorridor}
              disabled={!interactive}
              onChange={applyCorridor}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Text type="secondary">Lead source</Text>
            <Select
              data-testid="experiment-filter-lead-source"
              style={{ width: "100%", marginTop: 4 }}
              value={leadSource || undefined}
              allowClear
              placeholder="All sources"
              disabled={!interactive}
              onChange={(value) =>
                filterControls?.updateDashboardFilter?.(
                  "commercial",
                  "leadSource",
                  value || null
                )
              }
            >
              <Option value="whatsapp">WhatsApp</Option>
              <Option value="web">Web</Option>
              <Option value="referral">Referral</Option>
              <Option value="manual">Manual</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              data-testid="experiment-filter-reset"
              icon={<ReloadOutlined />}
              disabled={!interactive}
              onClick={() => filterControls?.resetDashboardFilters?.()}
              style={{ marginTop: 22 }}
            >
              Reset filters
            </Button>
          </Col>
        </Row>
      </Space>
    </SlotCard>
  );
}

function CorridorSelect({ value, disabled, onChange }) {
  const [corridors, setCorridors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        let list = [];
        try {
          list = await getActiveRouteCorridors();
        } catch (activeError) {
          // Fallback: list endpoint with active filter (same data, different path)
          const fallback = await getRouteCorridors({ active: true });
          list = fallback?.data || [];
          if (!cancelled) {
            setLoadError(
              activeError?.message
                ? `Active corridor shortcut failed (${activeError.message}). Using list fallback.`
                : "Using corridor list fallback."
            );
          }
        }
        if (!cancelled) setCorridors(Array.isArray(list) ? list : []);
      } catch (error) {
        if (!cancelled) {
          setCorridors([]);
          setLoadError(
            error?.status === 403 || /403|permission/i.test(error?.message || "")
              ? "No permission to load corridors (needs orders:view)."
              : "Corridors unavailable — filter still works for all corridors."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Select
        data-testid="experiment-filter-corridor"
        style={{ width: "100%", marginTop: 4 }}
        value={value || undefined}
        allowClear
        showSearch
        loading={loading}
        disabled={disabled}
        placeholder={loading ? "Loading corridors…" : "All corridors"}
        optionFilterProp="label"
        notFoundContent={
          loading
            ? "Loading…"
            : loadError
              ? "Could not load corridors"
              : "No active corridors"
        }
        onChange={(next) => onChange?.(next || null)}
      >
        {corridors.map((corridor) => {
          const label = corridor.description
            ? `${corridor.name} — ${corridor.description}`
            : corridor.name;
          return (
            <Option key={corridor.id || corridor.name} value={corridor.name} label={label}>
              {label}
            </Option>
          );
        })}
      </Select>
      {loadError ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {loadError}
        </Text>
      ) : null}
    </>
  );
}

function FilterSummaryText({ dashboardFilters }) {
  const parts = [];
  const datePreset = dashboardFilters?.time?.datePreset || dashboardFilters?.datePreset;
  const corridor =
    dashboardFilters?.operational?.routeCorridor ||
    dashboardFilters?.operational?.route;
  const source = dashboardFilters?.commercial?.leadSource;
  if (datePreset) parts.push(`Date: ${datePreset}`);
  if (corridor) parts.push(`Corridor: ${corridor}`);
  if (source) parts.push(`Source: ${source}`);
  if (!parts.length) return <Text type="secondary">No filters applied</Text>;
  return <Text type="secondary">{parts.join(" · ")}</Text>;
}

function criteriaText(item) {
  return typeof item === "string" ? item : item?.text || "";
}

function criteriaKey(item) {
  return criteriaText(item);
}

function decisionEmoji(status) {
  if (status === "validating") return "🟢";
  if (status === "invalidating") return "🔴";
  return "🟡";
}

function decisionTagColor(status) {
  if (status === "validating") return "success";
  if (status === "invalidating") return "error";
  return "warning";
}

function evidenceStateColor(status) {
  if (status === "validated") return "success";
  if (status === "early_signal") return "success";
  if (status === "contradicted" || status === "critical" || status === "failed") {
    return "error";
  }
  if (status === "not_tested") return "default";
  return "warning";
}

function formatEvidenceState(status) {
  if (status === "validated") return "Validated";
  if (status === "early_signal") return "Early signal";
  if (status === "weak_signal") return "Early signal";
  if (status === "contradicted" || status === "critical" || status === "failed") {
    return "Contradicted";
  }
  if (status === "not_tested") return "Not tested";
  return status || "—";
}

function formatStageStatus(status) {
  if (status === "failed") return "Failed";
  if (status === "not_tested") return "Not tested";
  if (status === "observed") return "Observed";
  return status || "—";
}

function formatMetricDisplay(value, unit) {
  if (value === null || value === undefined) return "—";
  if (unit === "percent") return `${formatNumber(value)}%`;
  if (unit === "minutes") return `${formatNumber(value)}m`;
  return formatNumber(value);
}

function formatPercent(value) {
  if (value === null || value === undefined) return "—";
  return `${formatNumber(value)}%`;
}

function acceptanceTagColor(acceptance) {
  if (acceptance === "yes") return "success";
  if (acceptance === "no") return "error";
  if (acceptance === "opened_no_response") return "warning";
  return "default";
}

function formatDeadlineRemaining(hoursRemaining, selectionDeadlineAt) {
  if (hoursRemaining == null && !selectionDeadlineAt) return "—";
  if (hoursRemaining != null && hoursRemaining < 0) return "Overdue";
  if (hoursRemaining != null && hoursRemaining <= 24) {
    return `${hoursRemaining}h left`;
  }
  if (selectionDeadlineAt) {
    try {
      return new Date(selectionDeadlineAt).toLocaleString();
    } catch {
      return String(selectionDeadlineAt);
    }
  }
  return "—";
}

function formatMoney(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return `R ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

export default ExperimentDashboardShell;
